import { Check, Info, Minus, Plus } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { adminNav } from '@/components/px/nav'
import {
  ASPIRATION_NOTICE,
  CELL_LEGEND,
  DERIVATION_NOTE,
  MODULE_COLUMN_LABELS,
  SECOND_FIRM_NOTICE,
  SUPER_ADMIN_CHROME,
  SUPER_ADMIN_DENSITY_NOTE,
  SUPER_ADMIN_HEADER,
  SUPER_ADMIN_LABELS,
  cellState,
  firmGroups,
  matrixColumnGroups,
  matrixColumns,
} from '@/demo/content/superAdmin'
import type { ModuleEntry } from '@/modules/architecture'
import { roleByKey, type Client, type ModuleInstallState } from '@/demo/data'
import type { FirmGroup } from '@/demo/content/superAdmin'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'

/**
 * `/admin/clients` — one group per implementer firm, each client against every
 * per-client installable module.
 *
 * ⚠ NO CELL IS SEEDED. Every one is computed by `moduleStateFor(client, id)`
 * from two seeded module sets, so ~170 cells cost zero invented figures and it
 * is structurally impossible for one to render blank (AC-5.3, AC-7.2).
 *
 * ⚠ The `ConceptBadge` here is MANDATORY: cross-firm scope is aspirational
 * against a runtime whose every route is `/:orgId/…`.
 *
 * Shape and label carry the cell's meaning — colour never carries it alone:
 * each glyph pairs an icon with an `sr-only` word and a `title`.
 */
export default function SuperAdminClientsPage() {
  const role = useAppSelector(selectActiveRoleKey)
  const groups = useMemo(() => firmGroups(role), [role])
  const columns = useMemo(() => matrixColumns(), [])
  const columnGroups = useMemo(() => matrixColumnGroups(), [])

  return (
    <AppShell
      moduleLabel={SUPER_ADMIN_CHROME.moduleLabel}
      breadcrumb={SUPER_ADMIN_CHROME.breadcrumb}
      contextPill={`${roleByKey(role).label} · ${groups.length} firms`.toUpperCase()}
      nav={adminNav(role)}
    >
      <div className="w-full">
        <div className="mb-3">
          <ConceptBadge />
        </div>
        <header className="max-w-3xl">
          <h1 className="font-serif text-2xl font-semibold text-foreground">
            {SUPER_ADMIN_HEADER.title}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{SUPER_ADMIN_HEADER.lede}</p>
        </header>

        <p className="mt-3 flex flex-wrap items-start gap-2 rounded-md border border-review-bg bg-review-bg px-3 py-2 text-xs text-review">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{ASPIRATION_NOTICE}</span>
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <SectionLabel>{SUPER_ADMIN_LABELS.cellStates}</SectionLabel>
          {(Object.keys(CELL_LEGEND) as ModuleInstallState[]).map((state) => (
            <span key={state} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <CellGlyph state={state} />
              {CELL_LEGEND[state]}
            </span>
          ))}
        </div>

        {groups.map((group) => (
          <FirmSection key={group.firm.id} group={group} columns={columns} columnGroups={columnGroups} />
        ))}

        <p className="mt-4 text-xs text-muted-foreground">
          {DERIVATION_NOTE.before}
          <span className="v-figure font-semibold tabular-nums text-foreground">{columns.length}</span>
          {DERIVATION_NOTE.after}
          <Link
            to="/architecture"
            className="text-accent-foreground underline-offset-4 hover:underline"
          >
            {DERIVATION_NOTE.linkLabel}
          </Link>
          .
        </p>

        <DensityNote>{SUPER_ADMIN_DENSITY_NOTE}</DensityNote>
      </div>
    </AppShell>
  )
}

function FirmSection({
  group,
  columns,
  columnGroups,
}: {
  group: FirmGroup
  columns: readonly ModuleEntry[]
  columnGroups: readonly { level: string; span: number }[]
}) {
  return (
    <section className="mt-4 rounded-lg border border-border bg-card px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg font-semibold text-foreground">{group.firm.name}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{group.summary}</p>
        </div>
        <StatusPill tone={group.isAnchor ? 'decided' : 'neutral'}>{group.pill}</StatusPill>
      </div>
      {!group.isAnchor && <p className="mt-2 text-xs text-muted-foreground">{SECOND_FIRM_NOTICE}</p>}

      <div className="mt-3">
        {/* The table scrolls, never the page — measured clean at 1440px. */}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <caption className="sr-only">{SUPER_ADMIN_HEADER.tableCaption}</caption>
            <thead>
              <tr>
                <th className="pb-1 pr-3 text-left" />
                <th className="pb-1 pr-2" />
                <th className="pb-1 pr-2" />
                <th className="pb-1 pr-3" />
                {columnGroups.map((cg) => (
                  <th
                    key={cg.level}
                    colSpan={cg.span}
                    scope="colgroup"
                    className="border-l border-border pb-1 text-center text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                  >
                    {cg.level}
                  </th>
                ))}
              </tr>
              <tr>
                {Object.values(SUPER_ADMIN_LABELS.columns).map((label, i) => (
                  <th
                    key={label}
                    scope="col"
                    className={cn(
                      'pb-2 pr-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
                      i === 3 ? 'text-right' : 'text-left'
                    )}
                  >
                    {label}
                  </th>
                ))}
                {columns.map((module, i) => (
                  <th
                    key={module.id}
                    scope="col"
                    title={module.name}
                    className={cn(
                      'w-[2.9rem] px-1 pb-2 align-bottom text-[0.625rem] font-medium leading-tight text-muted-foreground',
                      isGroupStart(columns, i) && 'border-l border-border'
                    )}
                  >
                    {MODULE_COLUMN_LABELS[module.id] ?? module.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.clients.map((client) => (
                <tr key={client.id} className="border-t border-hairline">
                  <th scope="row" className="py-1.5 pr-3 text-left text-sm font-medium text-foreground">
                    <ClientCell client={client} linked={group.isAnchor} />
                  </th>
                  <td className="pr-2 text-xs text-muted-foreground">{client.office}</td>
                  <td className="pr-2 text-xs text-muted-foreground">{client.vertical}</td>
                  <td className="v-figure pr-3 text-right text-xs tabular-nums text-muted-foreground">
                    {client.odooVersion}
                  </td>
                  {columns.map((module, i) => (
                    <td
                      key={module.id}
                      className={cn('px-1 py-1.5', isGroupStart(columns, i) && 'border-l border-border')}
                    >
                      <div className="flex justify-center">
                        <CellGlyph state={cellState(client, module)} label={module.name} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

/**
 * A client inside the ANCHOR firm links to its engagement; a second-firm client
 * is plain text, because no drill-down exists for it and inventing one would
 * be a dead link claiming depth this firm does not have.
 */
function ClientCell({ client, linked }: { client: Client; linked: boolean }) {
  if (!linked) return <span>{client.name}</span>
  return (
    <Link to="/clients" className="underline-offset-4 hover:underline">
      {client.name}
    </Link>
  )
}

function CellGlyph({ state, label }: { state: ModuleInstallState; label?: string }) {
  const word = CELL_LEGEND[state]
  const title = label ? `${label} — ${word}` : word
  const Icon = state === 'installed' ? Check : state === 'available' ? Plus : Minus
  return (
    <span
      title={title}
      className={cn(
        'flex h-5 w-5 items-center justify-center rounded-[0.25rem]',
        state === 'installed' && 'bg-primary text-primary-foreground',
        state === 'available' && 'border border-border bg-card text-muted-foreground',
        state === 'not-licensed' && 'bg-muted text-muted-foreground'
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      <span className="sr-only">{title}</span>
    </span>
  )
}

const isGroupStart = (columns: readonly ModuleEntry[], index: number): boolean =>
  index === 0 || columns[index]?.level !== columns[index - 1]?.level
